import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { LoggerService } from './logger.service';

/**
 * Network Service
 * Monitors network connectivity status and provides Observable for status changes.
 * Works in both web browsers and Capacitor native apps (iOS/Android).
 * 
 * For native apps, can be extended with @capacitor/network plugin for more detailed status.
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly isNative = Capacitor.isNativePlatform();
  
  private readonly onlineStatusSubject = new BehaviorSubject<boolean>(true);
  public readonly onlineStatus$: Observable<boolean> = this.onlineStatusSubject.asObservable();

  private initialized = false;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.init();
    }
  }

  /**
   * Initialize network status monitoring
   */
  private init(): void {
    if (this.initialized) return;
    
    // Set initial status
    const initialStatus = this.getCurrentStatus();
    this.onlineStatusSubject.next(initialStatus);

    // Listen to online/offline events
    if (typeof window !== 'undefined') {
      fromEvent(window, 'online').subscribe(() => {
        this.logger.log('Network status changed: ONLINE');
        this.onlineStatusSubject.next(true);
      });

      fromEvent(window, 'offline').subscribe(() => {
        this.logger.log('Network status changed: OFFLINE');
        this.onlineStatusSubject.next(false);
      });
    }

    // For native apps, we could use @capacitor/network plugin here
    // For now, we rely on browser events which also work in Capacitor WKWebView
    
    this.initialized = true;
  }

  /**
   * Get current network status
   */
  private getCurrentStatus(): boolean {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      return navigator.onLine;
    }
    // Default to online if we can't determine status
    return true;
  }

  /**
   * Check if device is currently online
   */
  isOnline(): boolean {
    return this.onlineStatusSubject.value;
  }

  /**
   * Check if device is currently offline
   */
  isOffline(): boolean {
    return !this.onlineStatusSubject.value;
  }

  /**
   * Get current network status synchronously
   */
  getStatus(): boolean {
    return this.onlineStatusSubject.value;
  }
}
